import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useGetClients } from "../../hooks/clients/useGetClients";
import { useDeleteClient } from "../../hooks/clients/useDeleteClient";
import { useDeleteAllClients } from "../../hooks/clients/useDeleteAllClients";
import { useRestoreAllClients } from "../../hooks/clients/useRestoreAllClients";
import { useForceDeleteAllClients } from "../../hooks/clients/useForceDeleteAllClients";

import useTableActions from "../../hooks/common/useTableActions";
import TableLayout from "../global/Table/TableLayout";
import ClientsTableRow from "./Table/ClientsTableRow";
import { ClientsTableColumns } from "./Table/ClientsTableColumns";
import { usePermissions } from "../../auth/PermissionsContext";

const ClientsTable = () => {
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng.startsWith("ar");
    const { has } = usePermissions();

    const perms = useMemo(
        () => ({
            list: has("viewAny-client"),
            create: has("create-client"),
            edit: has("update-client"),
            delete: has("delete-client"),
        }),
        [has]
    );

    const navigate = useNavigate();

    // Filter States
    const [trashedFilter, setTrashedFilter] = useState("");

    // CRUD Hooks
    const { deleteClientMutate, isPending: isDelete } = useDeleteClient();
    const { deleteAllClientsMutate, isPending: isDeleteAll } = useDeleteAllClients();
    const { restoreAllClientsMutate, isPending: isRestoreAll } = useRestoreAllClients();
    const { forceDeleteAllClientsMutate, isPending: isForceDeleteAll } = useForceDeleteAllClients();

    const {
        page,
        perPage,
        search,
        order,
        selectedIds,
        menuAnchorEl,
        setSearch,
        handleRequestSort,
        handleRowCheckboxClick,
        handleSelectAllClick,
        handleMenuOpen,
        handleMenuClose,
        handleEdit,
        handleMenuDelete,
        handleMenuRestore,
        handleBulkDelete,
        handleBulkRestore,
        handlePageChange,
        handlePerPageChange,
    } = useTableActions({
        basePath: "/clients",
        deleteSingle: deleteClientMutate,
        deleteAll: deleteAllClientsMutate,
        restoreSingle: (id, options) => restoreAllClientsMutate({ items: [id] }, options),
        restoreAll: restoreAllClientsMutate,
        forceDeleteSingle: (id, options) => forceDeleteAllClientsMutate({ items: [id] }, options),
        forceDeleteAll: forceDeleteAllClientsMutate,
        isArchive: trashedFilter === "only",
    });

    const params = useMemo(
        () => ({
            page,
            per_page: perPage,
            search: search || undefined,
            trashed: trashedFilter === "only" ? "only" : undefined,
            sort_by: 'created_at',
            sort_order: order,
        }),
        [page, perPage, search, trashedFilter, order]
    );

    const { isLoading, error, clients, meta } = useGetClients(params);

    // تجهيز الفلاتر لـ TableLayout
    // No additional filters for clients currently besides archive
    const filters = [];

    const columns = useMemo(() => ClientsTableColumns(isArabic), [isArabic]);

    return (
        <TableLayout
            title={{ ar: "العملاء", en: "Clients" }}
            columns={columns}
            items={clients}
            meta={meta}
            isLoading={isLoading}
            error={error}
            perms={perms}
            filters={filters}
            selectedIds={selectedIds}
            search={search}
            onSearchChange={setSearch}
            onSelectAllClick={(e) => handleSelectAllClick(e, clients)}
            onRowCheckboxClick={handleRowCheckboxClick}
            onPageChange={handlePageChange}
            onPerPageChange={handlePerPageChange}
            onBulkDelete={handleBulkDelete}
            onBulkRestore={handleBulkRestore}
            isBulkDeleting={isDeleteAll || isForceDeleteAll}
            isBulkRestoring={isRestoreAll}
            isArchive={trashedFilter === "only"}
            onAddClick={() => navigate("/clients/create")}
            addButtonLabel={{ ar: "إضافة عميل", en: "Add Client" }}
            page={page}
            perPage={perPage}
            order={order}
            onRequestSort={handleRequestSort}
            menuAnchorEl={menuAnchorEl}
            openMenu={Boolean(menuAnchorEl)}
            onMenuClose={handleMenuClose}
            onMenuEdit={handleEdit}
            onMenuDelete={handleMenuDelete}
            onMenuRestore={handleMenuRestore}
            onArchiveToggle={() => setTrashedFilter(prev => prev === "only" ? "" : "only")}
            isDeletingSingle={isDelete || isForceDeleteAll}
            renderRow={(params) => (
                <ClientsTableRow
                    {...params}
                    key={params.item.id}
                    client={params.item}
                    perms={perms}
                    onMenuOpen={handleMenuOpen}
                />
            )}
        />
    );
};

export default ClientsTable;